import { useState } from 'react';
import styled from 'styled-components';
import { LabelledTextField, Button, Switch, Alert, Label, Severity } from '@hamrolt/components';
import { PreloadedImage } from './PreloadedImage';
import { useForm } from 'react-hook-form';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { GET_NEWSLETTER_SIGN_UP, NEWSLETTER_RECIPIENT_EXISTS } from '../graphql/queries';
import { ADD_NEWSLETTER_RECIPIENT } from '../graphql/mutations';

type Alert = {
    severity: Severity;
    message: string;
};

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    componentId: string;
    imageId: string;
    locale: string;
}

interface FormData {
    firstName: string;
    email: string;
    agreement: boolean;
}

const Container = styled.div`
	display: grid;
	grid-gap: 48px;
	align-items: center;
	grid-template-columns: repeat(2, 1fr);
`;

const Heading = styled.h2`
	${({ theme }) => theme.typography.heading4};
	margin-bottom: 8px;
`;

const Body = styled.div`
`;

const Image = styled(PreloadedImage)`
	display: block;
	width: 100%;
`;

const Form = styled.form`
    display: grid;
    grid-gap: 24px;
    align-items: start;
    grid-template-columns: 1fr 1fr auto;
    grid-template-areas: 'firstName email submit' 'agreement agreement agreement';
`;

const SignIn = styled.div`
    display: grid;
    grid-gap: 24px;
`;

const Agreement = styled.div`
    grid-area: agreement;
    display: grid;
    grid-gap: 8px 16px;
    grid-template-columns: auto 1fr;
    grid-template-areas: 'agreementError agreementError';
`;

const AgreementError = styled(Label)`
    grid-area: agreementError;
`;

const AgreementLabel = styled(Label)`
    font-size: 14px;
`;

export const NewsletterSignUp = ({ componentId, imageId, locale, className }: Props) => {
    const [alert, setAlert] = useState<Alert | null>(null);
    const { data, loading } = useQuery(GET_NEWSLETTER_SIGN_UP, { variables: { id: componentId, imageId, locale } });
    const [recipientExists] = useLazyQuery(NEWSLETTER_RECIPIENT_EXISTS);
    const [addRecipient] = useMutation(ADD_NEWSLETTER_RECIPIENT);
    const { register, formState: { errors }, handleSubmit } = useForm<FormData>({ defaultValues: { firstName: '', email: '', agreement: false } });

    const onSubmit = (formData: FormData) => {
        recipientExists({ variables: { path: 'newsletterRecipients', email: formData.email } })
            .then(async ({ data: { newsletterRecipientExists } }) => {
                if (newsletterRecipientExists) {
                    setAlert({ severity: 'error', message: 'Ups, jesteś już zapisany do Newslettera' });
                } else {
                    await addRecipient({ variables: { path: 'newsletterRecipients', input: formData } });
                    setAlert({ severity: 'success', message: 'Dziękuję! Darmowy fragment e-booka zostanie przesłany do Ciebie w ciągu 48h. Jeśli nie widzisz wiadomości w skrzynce głównej, sprawdź folder oferty, spam i inne.' });
                }
            });
    };

    return loading
        ? <div>loading</div>
        : <Container className={className}>
            <Image {...data.preloadedImage} />
            <SignIn>
                <Heading>{data.newsletterSignUp.heading}</Heading>
                <Body>{data.newsletterSignUp.body}</Body>
                {alert && <Alert {...alert} />}
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <LabelledTextField
                        id="firstName"
                        forId="firstName"
                        severity="error"
                        endLabel={errors.firstName?.message}
                        placeholder="Imię"
                        {...register('firstName', { required: 'Imię jest wymagane' })}
                    />
                    <LabelledTextField
                        id="email"
                        forId="email"
                        severity="error"
                        endLabel={errors.email?.message}
                        placeholder="E-mail"
                        {...register('email', { required: 'E-mail jest wymagany' })}
                    />
                    <Button type="submit" role="primary" size="large">WYŚLIJ</Button>
                    <Agreement>
                        {errors.agreement && <AgreementError severity="error">{errors.agreement?.message}</AgreementError>}
                        <Switch id="agreement" {...register('agreement', { required: "Zgoda jest wymagana" })} />
                        <AgreementLabel htmlFor="agreement">Wyrażam zgodę na przetwarzanie podanych danych w celu pobrania poradnika. Wyrażam zgodę na otrzymywanie newslettera i informacji handlowych zgodnie z polityką prywatności na stronie www.twoj-projekt.pl</AgreementLabel>
                    </Agreement>
                </Form>
            </SignIn>
        </Container>
};